import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Calendar } from "lucide-react";
import { articles, getArticleBySlug } from "@/lib/articles";
import { SmartImage } from "@/components/SmartImage";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/blog/$slug")({
  head: ({ params }) => {
    const a = getArticleBySlug(params.slug);
    return {
      meta: [
        ...routeMeta(`/blog/${params.slug}`, "article"),
        { title: a ? `${a.title} | CCOB And Company PA` : "Article | CCOB And Company PA" },
        { name: "description", content: a?.excerpt ?? "Article from CCOB And Company PA" },
        { property: "og:title", content: a?.title ?? "Article" },
        { property: "og:description", content: a?.excerpt ?? "" },
      ],
      links: canonicalLinks(`/blog/${params.slug}`),
      scripts: a ? [{ type: "application/ld+json", children: JSON.stringify({ "@context": "https://schema.org", "@type": "Article", headline: a.title, description: a.excerpt, datePublished: a.date, mainEntityOfPage: `https://www.ccobandcompany.com/blog/${a.slug}`, publisher: { "@type": "Organization", name: "CCOB & Company PA" } }) }] : [],
    };
  },
  notFoundComponent: () => (
    <div className="container-x py-32 text-center">
      <h1 className="font-display text-4xl text-primary">Article not found</h1>
      <Link to="/blog" className="btn-primary mt-6 inline-flex">Back to blog</Link>
    </div>
  ),
  component: ArticlePage,
});

function renderBody(body: string) {
  return body.split(/\n\n+/).map((para, i) => {
    if (para.split("\n").every((l) => l.trim().startsWith("- "))) {
      return (
        <ul key={i} className="list-disc pl-6 my-5 space-y-2 text-foreground/80">
          {para.split("\n").map((l, j) => <li key={j}>{renderInline(l.replace(/^-\s+/, ""))}</li>)}
        </ul>
      );
    }
    return <p key={i} className="my-5 leading-relaxed text-foreground/80">{renderInline(para)}</p>;
  });
}

function renderInline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <strong key={i} className="text-primary">{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>
  );
}

function ArticlePage() {
  const { slug } = Route.useParams();
  const article = getArticleBySlug(slug);
  if (!article) throw notFound();
  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 3);
  return (
    <article className="pt-32 pb-24">
      <div className="container-x max-w-3xl">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"><ArrowLeft className="h-4 w-4" /> All articles</Link>
        <div className="mt-6 text-xs uppercase tracking-[0.25em] text-gold">{article.category}</div>
        <h1 className="font-display text-4xl md:text-5xl text-primary mt-3 leading-tight">{article.title}</h1>
        <div className="gold-line mt-5" />
        <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"><Calendar className="h-4 w-4" /> {article.date}</div>
        <SmartImage src={article.image} alt={article.title} width={1200} height={675} sizes="(max-width: 767px) 100vw, 768px" className="mt-8 rounded-2xl w-full aspect-[16/9] object-cover" />
        <div className="mt-8 text-base">{renderBody(article.body)}</div>
      </div>

      <div className="container-x max-w-5xl mt-20">
        <h2 className="font-display text-2xl text-primary mb-6">More from the blog</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {related.map((r) => (
            <Link key={r.slug} to="/blog/$slug" params={{ slug: r.slug }} className="bg-card border border-border rounded-2xl p-5 hover:border-gold transition">
              <div className="text-xs uppercase tracking-widest text-gold">{r.category}</div>
              <div className="font-display text-lg text-primary mt-2">{r.title}</div>
            </Link>
          ))}
        </div>
      </div>
    </article>
  );
}
